import { useState, useRef, useEffect, useMemo } from 'react';
import Checkbox from '@mui/material/Checkbox';
import { AnimatePresence, motion } from 'framer-motion';
import Classes from './style.module.css';

export default function ExpertiseDropDown({ selected, onChange }) {
    /* ───────────── Master list ───────────── */
    const master = useMemo(
        () => [
            'Finance', 'Sales', 'Accounting', 'Coding', 'Healthcare', 'Entertainment', 'Law',
            'Marketing', 'Education', 'Real Estate', 'Insurance', 'Energy', 'Entrepreneurship'
        ],
        []
    );

    /* ───────────── State ───────────── */
    const [customOpts, setCustomOpts] = useState([]); // user‑added items
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [showOtherInput, setShowOtherInput] = useState(false);
    const [otherValue, setOtherValue] = useState('');

    /* ───────────── Derived full list ───────────── */
    const fullOptions = useMemo(() => {
        const toObj = (v) => ({ value: v, label: v });
        return [
            ...master.map(toObj),
            { value: 'Other', label: 'Other' },  // always present
            ...customOpts,
        ];
    }, [master, customOpts]);

    /* ───────────── Refs ───────────── */
    const wrapperRef = useRef(null); // close on outside click
    const otherInputRef = useRef(null); // focus / scroll

    /* ───────────── Close dropdown on outside click ───────────── */
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    /* ───────────── Helpers ───────────── */
    const toggleSelect = (val) => {
        // “Other” handled separately
        if (val === 'Other') return;

        const next = selected.includes(val)
            ? selected.filter((v) => v !== val)
            : [...selected, val];
        onChange(next);
    };

    const handleOtherCheckbox = () => {
        setShowOtherInput((prev) => {
            const next = !prev;
            if (next) {
                // wait for panel to mount, then focus + scroll
                setTimeout(() => {
                    otherInputRef.current?.scrollIntoView({ behavior: 'smooth' });
                    otherInputRef.current?.focus();
                }, 0);
            } else {
                setOtherValue('');
            }
            return next;
        });
    };

    const addCustomExpertise = () => {
        const trimmed = otherValue.trim();
        if (!trimmed) return;

        const duplicate = fullOptions.some(
            (o) => o.value.toLowerCase() === trimmed.toLowerCase()
        );
        if (!duplicate) {
            setCustomOpts((prev) => [...prev, { value: trimmed, label: trimmed }]);
        }
        onChange([...selected, trimmed]); // auto‑select
        setOtherValue('');
        setShowOtherInput(false);        // collapse panel & uncheck box
    };

    /* ───────────── Header label ───────────── */
    const headerLabel = selected.length
        ? selected.slice(0, 5).join(', ') +
        (selected.length > 5 ? ` +${selected.length - 5}` : '')
        : <span style={{ color: '#c7c7c7' }}>Select areas of expertise</span>;

    /* ───────────── JSX ───────────── */
    return (
        <div className={Classes.dropDown} ref={wrapperRef}>
            {/* ─── Header ──────────────────────── */}
            <div
                className={Classes.SelectinputField}
                onClick={() => setOpen(!open)}
            >
                <p>{headerLabel}</p>
                <img
                    src="/assets/icons/arrow-down.svg"
                    alt="toggle"
                    style={{ height: '10px', transform: open ? 'rotate(180deg)' : undefined }}
                />
            </div>

            {/* ─── Body ───────────────────────── */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className={Classes.dropDownBody}
                    >
                        <div className={Classes.dropDownBodyInner}>
                            {/* Search bar */}
                            <div className={Classes.stickyCon}>
                                <div className={Classes.searcCon}>
                                    <input
                                        placeholder="Search Areas of Expertise"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* List */}
                            {fullOptions
                                .filter((o) =>
                                    o.label.toLowerCase().includes(search.toLowerCase())
                                )
                                .map((option, idx) => {
                                    const isSelected = selected.includes(option.value);
                                    const baseClass = `${Classes.dropDownElem} ${isSelected ? Classes.activeDropElem : ''
                                        }`;

                                    /* ---------- “Other” Row ---------- */
                                    if (option.value === 'Other') {
                                        return (
                                            <div
                                                key={idx}
                                                className={`${baseClass}`}
                                            >
                                                <Checkbox
                                                    checked={showOtherInput}
                                                    onChange={handleOtherCheckbox}
                                                    sx={{
                                                        color: 'var(--primary-color)',
                                                        padding: '5px',
                                                        '&.Mui-checked': { color: 'var(--primary-color)' },
                                                        '& .MuiSvgIcon-root': { fontSize: 18 },
                                                    }}
                                                />
                                                <label><h5>{option.label}</h5></label>
                                            </div>
                                        );
                                    }

                                    /* ---------- Regular rows ---------- */
                                    return (
                                        <div
                                            key={idx}
                                            className={baseClass}
                                            onClick={() => toggleSelect(option.value)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => toggleSelect(option.value)}
                                                sx={{
                                                    color: 'var(--primary-color)',
                                                    padding: '5px',
                                                    '&.Mui-checked': { color: 'var(--primary-color)' },
                                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                                }}
                                            />
                                            <label><h5>{option.label}</h5></label>
                                        </div>
                                    );
                                })}

                            {/* Custom‑input panel (visible only while “Other” is checked) */}
                            {showOtherInput && (
                                <div className={Classes.otherInputCon} ref={otherInputRef}>
                                    <input
                                        type="text"
                                        value={otherValue}
                                        onChange={(e) => setOtherValue(e.target.value)}
                                        placeholder="Enter custom expertise"
                                        className={Classes.searcCon}
                                    />
                                    <button
                                        type="button"
                                        className={Classes.otherAddBtn}
                                        onClick={addCustomExpertise}
                                    >
                                        Add
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
